import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronRight, Settings, Share2 } from 'lucide-react';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * PianoChannelsChart - Composant pour afficher les canaux d'acquisition Piano Analytics
 * Design identique au composant GA4ChannelsChart
 */


/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau rendait ses colonnes dans un ordre figé**, recopié à chaque niveau de la
 * hiérarchie. Choisir le modèle « E-commerce » réglait bien *quelles* métriques afficher, mais
 * pas leur ordre : la configuration portait une suite, l'écran en imposait une autre.
 *
 * Elles sont donc décrites ici, et le rendu parcourt `componentMetrics.order` — comme le font
 * déjà les composants Google Analytics équivalents.
 */
const COLONNES = {
  visites: { champ: 'visits', format: (v, f) => f(v), variation: true },
  visiteurs: { champ: 'visitors', format: (v, f) => f(v), variation: true },
  rebond: { champ: 'bounceRate', format: (v, f) => `${f(v, 1)}%`, variation: true, inverse: true },
  pages_vues: { champ: 'pageViews', format: (v, f) => f(v), variation: true },
  taux_conv: { champ: 'conversionRate', format: (v, f) => `${f(v, 2)}%`, variation: true },
  conv: { champ: 'conversions', format: (v, f) => f(v), variation: true },
  valeur_conv: { champ: 'conversionsValue', format: (v, f) => `${f(v, 2)}\u20ac`, variation: true },
  duree_moy: { champ: 'avgTimeOnSite', duree: true },
  panier_moyen: { champ: 'avgOrderValue', format: (v, f) => `${f(v, 2)}\u20ac` },
  pages_visite: { champ: 'actionsPerVisit', format: (v, f) => f(v, 2) },
  transactions: { champ: 'transactions', format: (v, f) => f(v, 0), variation: true },
  chiffre_affaires: { champ: 'totalRevenue', format: (v, f) => `${f(v, 2)}\u20ac`, variation: true },
  taux_transaction: { champ: 'transactionRate', format: (v, f) => `${f(v, 2)}%` }
};

/** La durée, en minutes:secondes. */
const formaterDuree = (secondes) => {
  const minutes = Math.floor((secondes || 0) / 60);
  const reste = Math.floor((secondes || 0) % 60);
  return `${minutes}:${reste.toString().padStart(2, '0')}`;
};

const PianoChannelsChart = ({
  channels = [],
  previousChannels = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'piano_channels'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'visits', direction: 'desc' });
  const [expandedChannels, setExpandedChannels] = useState({});
  const [expandedSources, setExpandedSources] = useState({}); // Pour le 2ème niveau d'expansion
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('piano_channels', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /**
   * L'intitulé d'une colonne, pris dans la table commune aux trois solutions de mesure.
   *
   * Les intitulés étaient écrits en dur — « Visites », « Visiteurs », « Pages/Visite ». Renommer
   * la table des libellés n'avait donc aucun effet ici, et le tableau continuait d'annoncer
   * autre chose que le reste de l'application : c'est ainsi que « Utilisateurs » restait
   * introuvable alors que la colonne était bien là.
   */
  const libelleColonne = (cle) => getMetricLabel(cle, 'piano_channels', accountId);

  /*
   * Les colonnes à rendre, **dans l'ordre du modèle**.
   *
   * `componentMetrics.order` porte la suite choisie ; `metrics` dit lesquelles sont actives.
   * Une clé inconnue de `COLONNES` vient d'un modèle conçu pour une autre famille : on l'écarte.
   */
  const colonnes = useMemo(() => {
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => COLONNES[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...COLONNES[cle] }));
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);


  /**
   * Ce que le camembert de droite représente.
   *
   * Il montrait les conversions quel que soit le tableau — y compris sur un compte e-commerce
   * où la colonne Conversions n'est pas affichée, et où ce sont les **transactions** qu'on
   * regarde. Le graphique et le tableau annonçaient alors deux choses différentes sans que rien
   * ne le dise.
   *
   * Les transactions l'emportent quand elles sont affichées — c'est le choix d'un compte
   * marchand, et le modèle « E-commerce » les active. À défaut, les conversions. Si aucune des
   * deux n'est à l'écran, le camembert disparaît : il n'aurait rien à représenter.
   */
  const mesureDuCamembert = isMetricActive('transactions')
    ? { cle: 'transactions', champ: 'transactions' }
    : (isMetricActive('conv') ? { cle: 'conv', champ: 'conversions' } : null);

  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Mapping des noms de canaux Piano vers des noms français
  const channelNameMapping = {
    'Direct': 'Acces direct',
    'direct': 'Acces direct',
    'Search engines': 'Moteurs de recherche',
    'search engines': 'Moteurs de recherche',
    'Social networks': 'Reseaux sociaux',
    'social networks': 'Reseaux sociaux',
    'Referral sites': 'Sites referents',
    'referral sites': 'Sites referents',
    'Referral': 'Sites referents',
    'referral': 'Sites referents',
    'Email': 'E-mail',
    'email': 'E-mail',
    'Campaigns': 'Campagnes',
    'campaigns': 'Campagnes',
    'Paid search': 'Recherche payante',
    'paid search': 'Recherche payante',
    'Display': 'Display',
    'display': 'Display',
    'Affiliates': 'Affilies',
    'affiliates': 'Affilies'
  };

  // Normaliser les données des canaux
  const channelData = useMemo(() => {
    if (!channels || !Array.isArray(channels) || channels.length === 0) {
      return [];
    }

    return channels.map(channel => {
      const rawName = channel.channel || channel.name || 'Autre';
      const name = channelNameMapping[rawName] || rawName;

      const visits = channel.visits || 0;
      const visitors = channel.visitors || channel.uniqueVisitors || 0;
      const pageViews = channel.pageViews || channel.pageviews || 0;
      const bounceRate = channel.bounceRate || 0;
      const conversions = channel.conversions || 0;
      const conversionsValue = channel.conversionsValue || channel.revenue || 0;

      // Normaliser les sources détaillées (niveau 2) avec leurs détails (niveau 3)
      const sources = (channel.sources || []).map(source => {
        const sourceVisits = source.visits || 0;
        const sourceConversions = source.conversions || 0;

        // Normaliser les détails (campagnes - niveau 3)
        const details = (source.details || []).map(detail => {
          const detailConversions = detail.conversions || 0;
          const detailConversionsValue = detail.conversionsValue || 0;
          return {
            label: detail.label || 'Inconnu',
            visits: detail.visits || 0,
            visitors: detail.visitors || 0,
            pageViews: detail.pageViews || 0,
            bounceRate: detail.bounceRate || 0,
            conversions: detailConversions,
            conversionRate: detail.visits > 0 ? (detailConversions / detail.visits * 100) : 0,
            conversionsValue: detailConversionsValue,
            avgTimeOnSite: detail.avgTimeOnSite || detail.avgSessionDuration || 0,
            avgOrderValue: detailConversions > 0 ? detailConversionsValue / detailConversions : 0,
            // Les mesures e-commerce, telles que le service les rend désormais — mêmes noms
            // que GA4 et Matomo.
            transactions: detail.transactions || 0,
            totalRevenue: detail.totalRevenue || 0,
            transactionRate: detail.transactionRate || 0
          };
        });

        const sourceConversionsValue = source.conversionsValue || 0;
        return {
          label: source.source || 'Inconnu',
          visits: sourceVisits,
          visitors: source.visitors || 0,
          pageViews: source.pageViews || 0,
          bounceRate: source.bounceRate || 0,
          conversions: sourceConversions,
          conversionRate: sourceVisits > 0 ? (sourceConversions / sourceVisits * 100) : 0,
          conversionsValue: sourceConversionsValue,
          avgTimeOnSite: source.avgTimeOnSite || source.avgSessionDuration || 0,
          avgOrderValue: sourceConversions > 0 ? sourceConversionsValue / sourceConversions : 0,
          transactions: source.transactions || 0,
          totalRevenue: source.totalRevenue || 0,
          transactionRate: source.transactionRate || 0,
          details,
          hasDetails: details.length > 0
        };
      });

      const avgTimeOnSite = channel.avgTimeOnSite || channel.avgSessionDuration || 0;
      const avgOrderValue = conversions > 0 ? conversionsValue / conversions : 0;

      return {
        name,
        rawName,
        visits,
        visitors,
        pageViews,
        bounceRate,
        conversions,
        conversionRate: visits > 0 ? (conversions / visits * 100) : 0,
        conversionsValue,
        avgTimeOnSite,
        avgOrderValue,
        transactions: channel.transactions || 0,
        totalRevenue: channel.totalRevenue || 0,
        transactionRate: channel.transactionRate || 0,
        sources,
        hasDetails: sources.length > 0
      };
    }).filter(c => c.visits > 0);
  }, [channels]);

  // Calcul des totaux
  const totals = useMemo(() => {
    if (!channelData.length) return {};

    const baseTotals = channelData.reduce((acc, channel) => ({
      visits: acc.visits + (channel.visits || 0),
      visitors: acc.visitors + (channel.visitors || 0),
      pageViews: acc.pageViews + (channel.pageViews || 0),
      conversions: acc.conversions + (channel.conversions || 0),
      conversionsValue: acc.conversionsValue + (channel.conversionsValue || 0),
      bounceRateSum: acc.bounceRateSum + ((channel.bounceRate || 0) * (channel.visits || 0)),
      avgTimeSum: acc.avgTimeSum + ((channel.avgTimeOnSite || 0) * (channel.visits || 0))
    }), { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0, bounceRateSum: 0, avgTimeSum: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      bounceRate: baseTotals.visits > 0 ? (baseTotals.bounceRateSum / baseTotals.visits) : 0,
      avgTimeOnSite: baseTotals.visits > 0 ? (baseTotals.avgTimeSum / baseTotals.visits) : 0,
      avgOrderValue: baseTotals.conversions > 0 ? (baseTotals.conversionsValue / baseTotals.conversions) : 0
    };
  }, [channelData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousChannels || !previousChannels.length) return null;

    const baseTotals = previousChannels.reduce((acc, channel) => ({
      visits: acc.visits + (channel.visits || 0),
      visitors: acc.visitors + (channel.visitors || 0),
      pageViews: acc.pageViews + (channel.pageViews || 0),
      conversions: acc.conversions + (channel.conversions || 0),
      conversionsValue: acc.conversionsValue + (channel.conversionsValue || 0),
      bounceRateSum: acc.bounceRateSum + ((channel.bounceRate || 0) * (channel.visits || 0)),
      avgTimeSum: acc.avgTimeSum + ((channel.avgTimeOnSite || 0) * (channel.visits || 0))
    }), { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0, bounceRateSum: 0, avgTimeSum: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      bounceRate: baseTotals.visits > 0 ? (baseTotals.bounceRateSum / baseTotals.visits) : 0,
      avgTimeOnSite: baseTotals.visits > 0 ? (baseTotals.avgTimeSum / baseTotals.visits) : 0,
      avgOrderValue: baseTotals.conversions > 0 ? (baseTotals.conversionsValue / baseTotals.conversions) : 0
    };
  }, [compareEnabled, previousChannels]);

  // Donnees triees
  const sortedChannels = useMemo(() => {
    if (!channelData.length) return [];

    const sorted = [...channelData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [channelData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedChannels.length / ITEMS_PER_PAGE);
  const paginatedChannels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedChannels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedChannels, currentPage]);

  // Donnees pour le pie chart Visites
  const visitsChartData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel.visits || 0,
        percentage: totals.visits > 0 ? ((channel.visits / totals.visits) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.visits]);

  // Donnees pour le pie chart Conversions
  /** Le total de la mesure représentée, calculé sur les lignes affichées. */
  const totalDuCamembert = useMemo(() => (
    mesureDuCamembert
      ? (channelData || []).reduce((somme, ligne) => somme + (ligne[mesureDuCamembert.champ] || 0), 0)
      : 0
  ), [channelData]);

  const conversionsChartData = useMemo(() => {
    if (!mesureDuCamembert) return [];
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel[mesureDuCamembert.champ] || 0,
        percentage: totalDuCamembert > 0 ? (((channel[mesureDuCamembert.champ] || 0) / totalDuCamembert) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.conversions]);

  // Donnees pour la legende
  const legendData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData.map((channel, index) => ({
      value: channel.name,
      color: COLORS[index % COLORS.length]
    }));
  }, [channelData]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handler pour deplier/replier un canal
  const toggleChannel = (channelName) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelName]: !prev[channelName]
    }));
  };

  // Handler pour deplier/replier une source (niveau 2)
  const toggleSource = (channelName, sourceLabel) => {
    const key = `${channelName}::${sourceLabel}`;
    setExpandedSources(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Ne pas afficher si pas de donnees ou en chargement
  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4>
            <Share2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Canaux
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des donnees...
        </div>
      </>
    );
  }

  if (!channelData.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
          <Share2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Canaux
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Canaux Piano"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '-' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs cote a cote */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Visites (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par canal</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={visitsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {visitsChartData.map((_, index) => (
                            <Cell key={`visits-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendeVisible.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px',
                          flexShrink: 0
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                    {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                    {legendeReste > 0 && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        + {legendeReste} autres
                      </div>
                    )}
                  </div>

                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{mesureDuCamembert ? `${libelleColonne(mesureDuCamembert.cle)} par canal` : 'Conversions par canal'}</h5>
                    {totalDuCamembert > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={conversionsChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {conversionsChartData.map((_, index) => (
                              <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        {/* Le modèle « Contenu » n'active ni l'une ni l'autre : le dire
                            vaut mieux qu'un cadre vide et sans titre. */}
                        {mesureDuCamembert
                          ? `Aucune ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`
                          : 'Activez Conversions ou Transactions pour ce graphique'}
                      </div>
                    )}
                </div>
              </div>
            </div>

          {/* Tableau detaille */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Canal {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC')}
                    </th>
                    {colonnes.map((colonne) => (
                      <th
                        key={colonne.cle}
                        onClick={() => handleSort(colonne.champ)}
                        className="sortable numeric"
                      >
                        {libelleColonne(colonne.cle)} {sortConfig.key === colonne.champ && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedChannels.map((channel, index) => {
                    const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
                    // Trouver le canal correspondant dans les données de la période précédente
                    const prev = compareEnabled && previousChannels ? previousChannels.find(p => p.channel === channel.rawName || p.name === channel.rawName) : null;
                    return (
                    <React.Fragment key={index}>
                      {/* Ligne principale du canal */}
                      <tr
                        className={channel.hasDetails ? 'expandable-row' : ''}
                        onClick={() => channel.hasDetails && toggleChannel(channel.name)}
                        style={{ cursor: channel.hasDetails ? 'pointer' : 'default' }}
                      >
                        <td className="device-name" style={{ whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: COLORS[globalIndex % COLORS.length],
                              marginRight: '8px',
                              flexShrink: 0
                            }}
                          ></span>
                          {channel.hasDetails && (
                            <ChevronRight size={12} style={{ marginRight: '4px', verticalAlign: 'middle', color: '#9ca3af', transform: expandedChannels[channel.name] ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                          )}
                          {channel.name}
                        </td>
                        {colonnes.map((colonne) => (
                          <td key={colonne.cle} className="numeric">
                            {colonne.duree
                              ? formaterDuree(channel[colonne.champ])
                              : colonne.format(channel[colonne.champ] || 0, formatNumber)}
                            {colonne.variation && prev && renderVariation(
                              calculateVariation(channel[colonne.champ] || 0, prev[colonne.champ] || 0),
                              colonne.inverse
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Niveau 2: Sources (google/cpc, bing/cpc, etc.) */}
                      {expandedChannels[channel.name] && channel.sources && channel.sources.map((source, sourceIndex) => {
                        const sourceKey = `${channel.name}::${source.label}`;
                        const isSourceExpanded = expandedSources[sourceKey];

                        return (
                          <React.Fragment key={`${index}-source-${sourceIndex}`}>
                            <tr
                              className={source.hasDetails ? 'detail-row expandable-row' : 'detail-row'}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (source.hasDetails) toggleSource(channel.name, source.label);
                              }}
                              style={{
                                backgroundColor: '#f9fafb',
                                fontSize: '0.9em',
                                cursor: source.hasDetails ? 'pointer' : 'default'
                              }}
                            >
                              <td className="device-name" style={{ paddingLeft: '36px' }}>
                                {source.hasDetails && (
                                  <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                    {isSourceExpanded ? (
                                      <ChevronDown size={12} />
                                    ) : (
                                      <ChevronRight size={12} />
                                    )}
                                  </span>
                                )}
                                {!source.hasDetails && <span style={{ color: '#6b7280', marginRight: '4px' }}>└</span>}
                                {source.label}
                              </td>
                              {colonnes.map((colonne) => (
                                <td key={colonne.cle} className="numeric" style={{ color: '#6b7280' }}>
                                  {colonne.duree
                                    ? formaterDuree(source[colonne.champ])
                                    : colonne.format(source[colonne.champ] || 0, formatNumber)}
                                </td>
                              ))}
                            </tr>

                            {/* Niveau 3: Détails/Campagnes */}
                            {isSourceExpanded && source.details && source.details.map((detail, detailIndex) => (
                              <tr
                                key={`${index}-source-${sourceIndex}-detail-${detailIndex}`}
                                className="detail-row"
                                style={{
                                  backgroundColor: '#f3f4f6',
                                  fontSize: '0.85em'
                                }}
                              >
                                <td className="device-name" style={{ paddingLeft: '60px' }}>
                                  <span style={{ color: '#9ca3af' }}>└</span> {detail.label}
                                </td>
                                {colonnes.map((colonne) => (
                                  <td key={colonne.cle} className="numeric" style={{ color: '#9ca3af' }}>
                                    {colonne.duree
                                      ? formaterDuree(detail[colonne.champ])
                                      : colonne.format(detail[colonne.champ] || 0, formatNumber)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );})}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {colonnes.map((colonne) => (
                      <td key={colonne.cle} className="numeric">
                        <strong>{colonne.duree
                          ? formaterDuree(totals[colonne.champ])
                          : colonne.format(totals[colonne.champ] || 0, formatNumber)}</strong>
                        {colonne.variation && previousTotals && renderVariation(
                          calculateVariation(totals[colonne.champ] || 0, previousTotals[colonne.champ] || 0),
                          colonne.inverse
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedChannels.length)} sur {sortedChannels.length} canaux
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Precedent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="piano_channels"
          accountId={accountId}
          componentTitle="Canaux"
        />
      )}
    </>
  );
};

export default PianoChannelsChart;
